import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Save, Wrench, Eye } from "lucide-react";

export default function AdminMaintenance() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newPassword, setNewPassword] = useState("");

  useEffect(() => {
    api.get("/site/settings").then((r) => setData(r.data)).finally(() => setLoading(false));
  }, []);

  async function save() {
    setSaving(true);
    try {
      const payload = {
        maintenance_enabled: !!data.maintenance_enabled,
        maintenance_title: data.maintenance_title || "",
        maintenance_message: data.maintenance_message || "",
        maintenance_until: data.maintenance_until || "",
      };
      if (newPassword) payload.maintenance_password = newPassword;
      const { data: saved } = await api.put("/site/settings", payload);
      setData(saved);
      setNewPassword("");
      toast.success("Gespeichert");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  }

  if (loading || !data) return <p className="text-sm text-neutral-500">Lade…</p>;

  return (
    <div data-testid="admin-maintenance">
      <h1 className="font-semibold text-3xl mb-1">Wartungsmodus</h1>
      <p className="text-sm text-neutral-500 mb-8">
        Wenn aktiv, sehen alle Besucher:innen eine Wartungsseite. Mitarbeiter:innen können sich mit dem unten gesetzten Passwort einloggen, der Admin-Bereich bleibt davon unberührt.
      </p>

      <div className="bg-white border border-brand-line p-6 sm:p-8 max-w-2xl space-y-6">
        {/* Toggle */}
        <label className="flex items-start gap-4 cursor-pointer">
          <input
            type="checkbox"
            checked={!!data.maintenance_enabled}
            onChange={(e) => setData({ ...data, maintenance_enabled: e.target.checked })}
            data-testid="maintenance-enabled"
            className="h-5 w-5 accent-[hsl(var(--brand-green-dark))] mt-0.5"
          />
          <div>
            <span className="text-base font-medium text-neutral-900 inline-flex items-center gap-2">
              <Wrench className="h-4 w-4" /> Wartungsmodus aktivieren
            </span>
            <p className="text-xs text-neutral-500 mt-1">
              Status: <strong className={data.maintenance_enabled ? "text-red-600" : "text-[hsl(var(--brand-green-strong))]"}>
                {data.maintenance_enabled ? "Wartung AKTIV — Webseite gesperrt" : "Webseite öffentlich erreichbar"}
              </strong>
            </p>
          </div>
        </label>

        <hr className="border-brand-line" />

        <label className="block">
          <span className="block text-xs text-neutral-500 mb-1.5">Überschrift auf der Wartungsseite</span>
          <input
            value={data.maintenance_title || ""}
            onChange={(e) => setData({ ...data, maintenance_title: e.target.value })}
            data-testid="maintenance-title"
            className="w-full border border-brand-line px-3 py-2.5 text-sm focus:outline-none focus:border-[hsl(var(--brand-green-strong))]"
          />
        </label>

        <label className="block">
          <span className="block text-xs text-neutral-500 mb-1.5">Hinweistext</span>
          <textarea
            value={data.maintenance_message || ""}
            onChange={(e) => setData({ ...data, maintenance_message: e.target.value })}
            data-testid="maintenance-message"
            className="w-full border border-brand-line px-3 py-2.5 text-sm min-h-[100px] focus:outline-none focus:border-[hsl(var(--brand-green-strong))]"
          />
        </label>

        <label className="block">
          <span className="block text-xs text-neutral-500 mb-1.5">Voraussichtlich erreichbar ab (optional)</span>
          <input
            value={data.maintenance_until || ""}
            onChange={(e) => setData({ ...data, maintenance_until: e.target.value })}
            data-testid="maintenance-until"
            placeholder="z. B. heute 18:00 Uhr"
            className="w-full sm:max-w-xs border border-brand-line px-3 py-2.5 text-sm focus:outline-none focus:border-[hsl(var(--brand-green-strong))]"
          />
        </label>

        <hr className="border-brand-line" />

        <label className="block">
          <span className="block text-xs text-neutral-500 mb-1.5">Mitarbeiter-Passwort ändern (leer lassen = unverändert)</span>
          <input
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            data-testid="maintenance-password-new"
            placeholder="Neues Passwort"
            className="w-full sm:max-w-md border border-brand-line px-3 py-2.5 text-sm focus:outline-none focus:border-[hsl(var(--brand-green-strong))]"
          />
          <p className="text-xs text-neutral-400 mt-1.5">
            Mit diesem Passwort kann der Wartungsmodus auf der Webseite umgangen werden („Mitarbeiter-Zugang").
          </p>
        </label>

        <div className="flex items-center justify-between pt-2">
          <a
            href="/"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 text-sm text-neutral-500 hover:text-[hsl(var(--brand-green-strong))]"
          >
            <Eye className="h-4 w-4" /> Webseite öffnen
          </a>
          <button
            onClick={save}
            disabled={saving}
            data-testid="maintenance-save"
            className="inline-flex items-center gap-2 bg-[hsl(var(--brand-green-dark))] text-white px-6 py-2.5 text-sm hover:bg-[hsl(var(--brand-green-strong))] disabled:opacity-50"
          >
            <Save className="h-4 w-4" /> {saving ? "Speichere…" : "Speichern"}
          </button>
        </div>
      </div>
    </div>
  );
}
